// src/lib/owners/optimisticOwners.ts
//
// Pure helpers for the project-record OWNER picker's SANDBOX (walkthrough)
// selection persistence. Extracted from the inline commitOwners closure in
// src/App.tsx so the invariant can be unit-guarded under src/lib/** — see
// docs/REGRESSIONS.md.
//
// THE BUG this fixes: during a knowledge-base walkthrough on the (ephemeral)
// Test Project record, clicking an owner in "SELECT OWNERS" did nothing — the
// checkbox reverted. Root cause: commitOwners PATCHes /sub-admin/projects/:id/
// owner (blocked by the walkthrough fetch-shim with a synthetic 200) then
// re-fetches the project via fetchCrmProject — which in tutorial mode is served
// from the fixture buildTestProject() that hardcodes owner_id/owner_name = null
// and has NO owners_members. setProjectDetailData(fresh) then wiped the just-made
// selection, so `isSelected` (derived from projectDetailData.owners_members)
// went empty and the checkbox unchecked.
//
// THE FIX (tutorial-scoped): in a walkthrough, skip the destructive fixture
// refetch and update projectDetailData.owners_members OPTIMISTICALLY from the
// committed ids, mirroring the { user_id, name } shape the picker + senior
// section consume. Real (non-walkthrough) projects keep the existing
// PATCH + fetchCrmProject refetch behaviour unchanged.

/** Minimal owner-pool entry shape used to build an optimistic member. */
export interface OwnerPoolEntry {
  id: string | number
  name?: string | null
  last_name?: string | null
  company_id?: string | number | null
  company_name?: string | null
  effective_company_id?: string | number | null
}

/** The owners_members member shape consumed by the owner picker + senior section. */
export interface OwnerMember {
  user_id: string
  name: string
  company_id: string | null
  company_name: string | null
}

/**
 * True when owner selection should be applied optimistically (local state only,
 * no destructive fixture refetch) rather than via the normal PATCH+refetch —
 * i.e. while a knowledge-base walkthrough is active.
 */
export function shouldUseOptimisticOwnerUpdate(walkthroughActive: boolean): boolean {
  return !!walkthroughActive
}

const fullName = (u: OwnerPoolEntry): string => {
  const composed = `${u?.name || ''} ${u?.last_name || ''}`.trim()
  return composed || String(u?.name || '')
}

const asStr = (v: string | number | null | undefined): string | null =>
  v == null || v === '' ? null : String(v)

/**
 * Build an owners_members array from the committed owner ids, resolving each
 * member's display fields from the owner pool. Ids with no pool match still
 * yield a member (so the checkbox stays checked) with an empty name.
 * Order follows nextIds. Result matches the { user_id, name } shape App.tsx's
 * `isSelected` (owners_members.some(o => o.user_id === u.id)) and the
 * comma-joined owner-button label consume, plus company fields for parity with
 * the real API row.
 */
export function buildOptimisticOwnersMembers(
  ownerPool: OwnerPoolEntry[],
  nextIds: Array<string | number>,
): OwnerMember[] {
  const byId = new Map<string, OwnerPoolEntry>()
  for (const u of Array.isArray(ownerPool) ? ownerPool : []) {
    byId.set(String(u.id), u)
  }
  return (Array.isArray(nextIds) ? nextIds : []).map((rawId) => {
    const id = String(rawId)
    const u = byId.get(id)
    return {
      user_id: id,
      name: u ? fullName(u) : '',
      company_id: u ? asStr(u.company_id) : null,
      company_name: u ? (u.company_name ?? null) : null,
    }
  })
}
